
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { getSentimentColor, getSentimentText } from "./sentimentService";

interface OverallSentimentChartProps {
  overall: number;
  categories: {
    positive: number;
    neutral: number;
    negative: number;
  };
}

export const OverallSentimentChart: React.FC<OverallSentimentChartProps> = ({
  overall,
  categories
}) => {
  const renderCustomizedLabel = (props: any) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="black" 
        textAnchor="middle" 
        dominantBaseline="central"
        fontSize="15px"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Card className="bg-secondary/50 border-2 border-primary rounded">
      <CardHeader>
        <CardTitle className="text-white">Overall Sentiment Analysis</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center justify-center space-y-4">
        <div className="text-5xl font-bold mb-2 text-center">
          <span className={getSentimentColor(overall)}>
            {getSentimentText(overall)}
          </span>
        </div>
        <div className="text-lg">
          Overall Score: <span className={getSentimentColor(overall)}>{(overall * 100).toFixed(0)}</span>
        </div>
        <div className="flex justify-center w-full h-64">
          <ChartContainer config={{
            positive: { color: '#4ade80' },
            neutral: { color: '#facc15' },
            negative: { color: '#f87171' }
          }} className="w-full h-full">
            <PieChart>
              <Pie 
                data={[
                  { name: 'Positive', value: categories.positive },
                  { name: 'Neutral', value: categories.neutral },
                  { name: 'Negative', value: categories.negative }
                ]} 
                cx="50%" 
                cy="50%" 
                labelLine={false} 
                outerRadius={110} 
                fill="#8884d8" 
                dataKey="value" 
                label={renderCustomizedLabel}
              >
                <Cell name="Positive" fill="#4ade80" />
                <Cell name="Neutral" fill="#facc15" />
                <Cell name="Negative" fill="#f87171" />
              </Pie>
              <Tooltip />
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                align="center"
                formatter={(value) => <span className="text-white">{value}</span>}
              />
            </PieChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};
